import * as THREE from 'three';
import type { LevelDef, PathDef, V2 } from '../levels/types';

export interface BuiltScene {
  scene: THREE.Scene;
  coneMeshes: THREE.Object3D[];
  zoneMeshes: Map<string, THREE.Mesh>;
  dispose(): void;
}

/** Generador congruencial simple para texturas estables (sin Math.random). */
function lcg(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

function makeAsphaltTexture(): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#3c3f45';
  ctx.fillRect(0, 0, size, size);
  const rnd = lcg(1234567);
  for (let i = 0; i < 2600; i++) {
    const shade = 46 + Math.floor(rnd() * 42);
    ctx.fillStyle = `rgb(${shade},${shade + 2},${shade + 6})`;
    ctx.fillRect(rnd() * size, rnd() * size, 1.6, 1.6);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

function makeSignTexture(kind: 'stop' | 'yield' | 'speed30' | 'speed50'): THREE.CanvasTexture {
  const s = 128;
  const canvas = document.createElement('canvas');
  canvas.width = s;
  canvas.height = s;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, s, s);
  const cx = s / 2;
  if (kind === 'stop') {
    ctx.fillStyle = '#c1121f';
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const a = (Math.PI / 8) * (2 * i + 1);
      const x = cx + Math.cos(a) * 60;
      const y = cx + Math.sin(a) * 60;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 5;
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 34px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('STOP', cx, cx + 2);
  } else if (kind === 'yield') {
    ctx.fillStyle = '#c1121f';
    ctx.beginPath();
    ctx.moveTo(6, 14);
    ctx.lineTo(s - 6, 14);
    ctx.lineTo(cx, s - 8);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.moveTo(26, 26);
    ctx.lineTo(s - 26, 26);
    ctx.lineTo(cx, s - 30);
    ctx.closePath();
    ctx.fill();
  } else {
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(cx, cx, 58, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#c1121f';
    ctx.lineWidth = 14;
    ctx.beginPath();
    ctx.arc(cx, cx, 50, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = '#111';
    ctx.font = 'bold 44px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(kind === 'speed30' ? '30' : '50', cx, cx + 2);
  }
  return new THREE.CanvasTexture(canvas);
}

/** Construye una cinta (ribbon) plana a lo largo de una polilínea. */
function ribbonGeometry(points: V2[], width: number, y: number): THREE.BufferGeometry {
  const half = width / 2;
  const verts: number[] = [];
  const idx: number[] = [];
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const [x, z] = points[i]!;
    const [px, pz] = points[Math.max(0, i - 1)]!;
    const [nx, nz] = points[Math.min(n - 1, i + 1)]!;
    let dx = nx - px;
    let dz = nz - pz;
    const len = Math.hypot(dx, dz) || 1;
    dx /= len;
    dz /= len;
    // perpendicular en el plano XZ
    const ox = -dz * half;
    const oz = dx * half;
    verts.push(x + ox, y, z + oz, x - ox, y, z - oz);
    if (i > 0) {
      const a = (i - 1) * 2;
      idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  return geo;
}

/** Divide una polilínea en tramos (para líneas discontinuas). */
function dashedSegments(points: V2[], dash: number, gap: number): V2[][] {
  const segs: V2[][] = [];
  let current: V2[] = [];
  let distIntoCycle = 0;
  const cycle = dash + gap;
  for (let i = 1; i < points.length; i++) {
    let [ax, az] = points[i - 1]!;
    const [bx, bz] = points[i]!;
    let segLen = Math.hypot(bx - ax, bz - az);
    while (segLen > 1e-6) {
      const inDash = distIntoCycle < dash;
      const remainingPhase = inDash ? dash - distIntoCycle : cycle - distIntoCycle;
      const stepLen = Math.min(segLen, remainingPhase);
      const t = stepLen / segLen;
      const mx = ax + (bx - ax) * t;
      const mz = az + (bz - az) * t;
      if (inDash) {
        if (current.length === 0) current.push([ax, az]);
        current.push([mx, mz]);
      } else if (current.length > 1) {
        segs.push(current);
        current = [];
      } else {
        current = [];
      }
      distIntoCycle = (distIntoCycle + stepLen) % cycle;
      ax = mx;
      az = mz;
      segLen -= stepLen;
    }
  }
  if (current.length > 1) segs.push(current);
  return segs;
}

/** Desplaza una polilínea perpendicularmente (aprox., válido para curvas suaves). */
function offsetPolyline(points: V2[], offset: number): V2[] {
  const out: V2[] = [];
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const [px, pz] = points[Math.max(0, i - 1)]!;
    const [x, z] = points[i]!;
    const [nx, nz] = points[Math.min(n - 1, i + 1)]!;
    let dx = nx - px;
    let dz = nz - pz;
    const len = Math.hypot(dx, dz) || 1;
    dx /= len;
    dz /= len;
    out.push([x - dz * offset, z + dx * offset]);
  }
  return out;
}

const ZONE_COLORS: Record<string, number> = {
  target: 0x2fd06e,
  stop: 0xe2574c,
  bay: 0x3fa0e8,
  start: 0xcccccc,
  speed: 0xffffff,
};

export function buildScene(level: LevelDef): BuiltScene {
  const scene = new THREE.Scene();
  const weather = level.weather.type;
  const night = weather === 'night';
  const disposables: Array<{ dispose(): void }> = [];
  const track = <T extends { dispose(): void }>(r: T): T => {
    disposables.push(r);
    return r;
  };

  scene.background = new THREE.Color(night ? 0x0a1120 : 0x9ec3e8);
  scene.fog = new THREE.FogExp2(
    night ? 0x0a1120 : 0xa9c4dd,
    weather === 'fog' ? 0.02 : weather === 'rain' ? 0.006 : 0.0026,
  );

  const hemi = new THREE.HemisphereLight(0xbfd7f0, 0x5e6b57, night ? 0.15 : 0.95);
  scene.add(hemi);
  const sun = new THREE.DirectionalLight(night ? 0x8899cc : 0xfff2df, night ? 0.12 : 2.4);
  sun.position.set(70, 90, 40);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.left = -90;
  sun.shadow.camera.right = 90;
  sun.shadow.camera.top = 90;
  sun.shadow.camera.bottom = -90;
  sun.shadow.camera.far = 260;
  sun.shadow.bias = -0.0004;
  scene.add(sun);

  // Suelo
  const [gx, gz] = level.environment.groundSize;
  const asphalt = track(makeAsphaltTexture());
  asphalt.repeat.set(gx / 6, gz / 6);
  const groundMat = track(new THREE.MeshStandardMaterial({ map: asphalt, roughness: 0.95 }));
  const ground = new THREE.Mesh(track(new THREE.PlaneGeometry(gx, gz)), groundMat);
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  scene.add(ground);

  // Césped exterior
  const grassMat = track(new THREE.MeshStandardMaterial({ color: 0x5e7a4e, roughness: 1 }));
  const grass = new THREE.Mesh(track(new THREE.PlaneGeometry(gx * 4, gz * 4)), grassMat);
  grass.rotation.x = -Math.PI / 2;
  grass.position.y = -0.03;
  grass.receiveShadow = true;
  scene.add(grass);

  // Silueta urbana decorativa alrededor
  const skyMat = track(new THREE.MeshStandardMaterial({ color: 0x7c8894, roughness: 0.9 }));
  const heights = [10, 16, 8, 20, 12, 9, 18, 14, 7, 22, 11, 15];
  let hIdx = 0;
  const ringX = gx / 2 + 26;
  const ringZ = gz / 2 + 26;
  for (let i = -3; i <= 3; i++) {
    for (const [bx, bz] of [
      [i * (gx / 6), -ringZ],
      [i * (gx / 6), ringZ],
      [-ringX, i * (gz / 6)],
      [ringX, i * (gz / 6)],
    ] as V2[]) {
      const h = heights[hIdx++ % heights.length]!;
      const b = new THREE.Mesh(new THREE.BoxGeometry(14, h, 14), skyMat);
      b.position.set(bx, h / 2 - 0.5, bz);
      scene.add(b);
    }
  }

  const coneMeshes: THREE.Object3D[] = [];
  const zoneMeshes = new Map<string, THREE.Mesh>();

  const whiteMat = track(
    new THREE.MeshBasicMaterial({ color: 0xe8e8e8, polygonOffset: true, polygonOffsetFactor: -1 }),
  );
  const yellowMat = track(
    new THREE.MeshBasicMaterial({ color: 0xd9b23a, polygonOffset: true, polygonOffsetFactor: -1 }),
  );
  const roadMat = track(
    new THREE.MeshStandardMaterial({ color: 0x47494e, roughness: 0.9, polygonOffset: true, polygonOffsetFactor: -0.5 }),
  );

  const addRibbon = (pts: V2[], width: number, mat: THREE.Material, y: number): void => {
    const mesh = new THREE.Mesh(track(ribbonGeometry(pts, width, y)), mat);
    mesh.receiveShadow = true;
    scene.add(mesh);
  };

  const coneTireMat = track(new THREE.MeshStandardMaterial({ color: 0xff7a28, roughness: 0.6 }));
  const coneBandMat = track(new THREE.MeshStandardMaterial({ color: 0xf2f2f2, roughness: 0.5 }));
  const coneBaseMat = track(new THREE.MeshStandardMaterial({ color: 0xcf5f1d, roughness: 0.8 }));
  const coneGeo = track(new THREE.CylinderGeometry(0.035, 0.15, 0.5, 14));
  const coneBandGeo = track(new THREE.CylinderGeometry(0.09, 0.115, 0.1, 14));
  const coneBaseGeo = track(new THREE.BoxGeometry(0.32, 0.045, 0.32));

  for (const prop of level.environment.props) {
    switch (prop.type) {
      case 'box': {
        const mat = new THREE.MeshStandardMaterial({
          color: new THREE.Color(prop.color ?? '#8a8f98'),
          roughness: 0.85,
        });
        track(mat);
        const mesh = new THREE.Mesh(
          track(new THREE.BoxGeometry(prop.size[0], prop.size[1], prop.size[2])),
          mat,
        );
        mesh.position.set(prop.pos[0], prop.size[1] / 2, prop.pos[1]);
        mesh.rotation.y = ((prop.rotYDeg ?? 0) * Math.PI) / 180;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        scene.add(mesh);
        break;
      }
      case 'ramp': {
        const mat = track(new THREE.MeshStandardMaterial({ color: 0x565b63, roughness: 0.9 }));
        const mesh = new THREE.Mesh(
          track(new THREE.BoxGeometry(prop.size[0], prop.size[1], prop.size[2])),
          mat,
        );
        const yaw = ((prop.rotYDeg ?? 0) * Math.PI) / 180;
        const pitch = (prop.pitchDeg * Math.PI) / 180;
        mesh.quaternion.setFromEuler(new THREE.Euler(pitch, yaw, 0, 'YXZ'));
        mesh.position.set(prop.pos[0], 0, prop.pos[1]);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        scene.add(mesh);
        break;
      }
      case 'cone': {
        const g = new THREE.Group();
        const cone = new THREE.Mesh(coneGeo, coneTireMat);
        cone.position.y = 0; // el grupo se coloca en el centro físico (y≈0.26)
        cone.castShadow = true;
        const band = new THREE.Mesh(coneBandGeo, coneBandMat);
        band.position.y = 0.02;
        const base = new THREE.Mesh(coneBaseGeo, coneBaseMat);
        base.position.y = -0.23;
        g.add(cone, band, base);
        g.position.set(prop.pos[0], 0.26, prop.pos[1]);
        scene.add(g);
        coneMeshes.push(g);
        break;
      }
      case 'lane': {
        const isWide = prop.width > 1;
        if (isWide) {
          addRibbon(prop.points, prop.width, roadMat, 0.008);
          const edgeL = offsetPolyline(prop.points, prop.width / 2 - 0.08);
          const edgeR = offsetPolyline(prop.points, -(prop.width / 2 - 0.08));
          addRibbon(edgeL, 0.13, whiteMat, 0.014);
          addRibbon(edgeR, 0.13, whiteMat, 0.014);
        } else {
          const mat = prop.color === 'yellow' ? yellowMat : whiteMat;
          if (prop.dashed) {
            for (const seg of dashedSegments(prop.points, 2.5, 2.5))
              addRibbon(seg, prop.width, mat, 0.014);
          } else {
            addRibbon(prop.points, prop.width, mat, 0.014);
          }
        }
        break;
      }
      case 'zone': {
        if (prop.visible === false) break;
        const color = ZONE_COLORS[prop.kind] ?? 0xffffff;
        const mat = track(
          new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.16,
            polygonOffset: true,
            polygonOffsetFactor: -2,
          }),
        );
        const mesh = new THREE.Mesh(track(new THREE.PlaneGeometry(prop.size[0], prop.size[1])), mat);
        // La inclinación opcional permite pintar zonas sobre rampas
        mesh.rotation.x = -Math.PI / 2 + ((prop.pitchDeg ?? 0) * Math.PI) / 180;
        mesh.rotation.z = -((prop.rotYDeg ?? 0) * Math.PI) / 180;
        mesh.position.set(prop.pos[0], prop.y ?? 0.02, prop.pos[1]);
        scene.add(mesh);
        zoneMeshes.set(prop.id, mesh);
        break;
      }
      case 'sign': {
        const tex = track(makeSignTexture(prop.sign));
        const poleMat = track(new THREE.MeshStandardMaterial({ color: 0x9aa0a8 }));
        const pole = new THREE.Mesh(track(new THREE.CylinderGeometry(0.04, 0.04, 2.3, 8)), poleMat);
        pole.position.set(prop.pos[0], 1.15, prop.pos[1]);
        pole.castShadow = true;
        scene.add(pole);
        const faceMat = track(new THREE.MeshBasicMaterial({ map: tex, transparent: true }));
        const face = new THREE.Mesh(track(new THREE.PlaneGeometry(0.68, 0.68)), faceMat);
        face.position.set(prop.pos[0], 2.05, prop.pos[1]);
        face.rotation.y = (prop.rotYDeg * Math.PI) / 180;
        scene.add(face);
        break;
      }
    }
  }

  // Trayectoria de guía sutil (flechas / línea central discontinua) para follow_path
  const guideMat = track(
    new THREE.MeshBasicMaterial({
      color: 0x9fd4ff,
      transparent: true,
      opacity: 0.35,
      polygonOffset: true,
      polygonOffsetFactor: -1.5,
    }),
  );
  for (const path of Object.values(level.environment.paths ?? {})) {
    for (const seg of dashedSegments(path.points, 1.4, 3.2)) addRibbon(seg, 0.3, guideMat, 0.018);
  }

  return {
    scene,
    coneMeshes,
    zoneMeshes,
    dispose: () => {
      for (const r of disposables) r.dispose();
    },
  };
}

export type { PathDef };
